import { finder } from '@medv/finder';
import { ProboLogger, getParentNode } from '@probolabs/probo-shared';

export const highlighterLogger = new ProboLogger('highlighter');

export function setLoggerDebugLevel(level) {
  highlighterLogger.setLogLevel(level);
}

// Get text content of an element (recursively including child elements)
export function getElementText(element) {
  if (!element) return '';
  return element.textContent || '';
}

// Get placeholder text of an element
export function getElementPlaceholder(element) {
  if (!element) return '';
  return element.placeholder || '';
}

// Get CSS selector of an element (not recursively, just for a single element)
export function getElementCssSelector(element) {
  if (!(element instanceof Element)) return '';
  
  const tag = element.tagName.toLowerCase();
  let selector = tag;
        
  // Find nth-of-type for elements without ID
  let nth = 1;
  let sibling = element.previousElementSibling;
  while (sibling) {
    if (sibling.nodeName.toLowerCase() === tag) nth++;
    sibling = sibling.previousElementSibling;
  }
  if (nth > 1 || element.nextElementSibling) selector += `:nth-of-type(${nth})`;
  
  return selector;
}

// Helper function to check if element is a form control
function isFormControl(elementInfo) {
  return /^(input|select|textarea|button|label)$/i.test(elementInfo.tag);
}

// Helper function to check if element is a dropdown item
const isDropdownItem = (elementInfo) => {
  const dropdownPatterns = [
    /dropdown[-_]?item/i,    // matches: dropdown-item, dropdownitem, dropdown_item
    /menu[-_]?item/i,        // matches: menu-item, menuitem, menu_item
    /dropdown[-_]?link/i,    // matches: dropdown-link, dropdownlink, dropdown_link
    /list[-_]?item/i,       // matches: list-item, listitem, list_item
    /select[-_]?item/i,     // matches: select-item, selectitem, select_item  
  ];

  const rolePatterns = [
    /menu[-_]?item/i,       // matches: menuitem, menu-item
    /option/i,              // matches: option
    /list[-_]?item/i,      // matches: listitem, list-item
    /tree[-_]?item/i       // matches: treeitem, tree-item
  ];

  const hasMatchingClass = elementInfo.element.className && 
                          dropdownPatterns.some(pattern => 
                            pattern.test(elementInfo.element.className)
                          );

  const hasMatchingRole = elementInfo.element.getAttribute('role') && 
                         rolePatterns.some(pattern => 
                           pattern.test(elementInfo.element.getAttribute('role'))
                         );

  return hasMatchingClass || hasMatchingRole;
};

/**
 * Element Query Utilities
 */

export function getAllDocumentElementsIncludingShadow(selectors, root = document) {
  const elements = Array.from(root.querySelectorAll(selectors));

  root.querySelectorAll('*').forEach(el => {
      if (el.shadowRoot) {
          elements.push(...getAllDocumentElementsIncludingShadow(selectors, el.shadowRoot));
      }
  });
  return elements;
}

export function getAllFrames(root = document) {
  const result = [root];
  const frames = getAllDocumentElementsIncludingShadow('frame, iframe', root);  
  frames.forEach(frame => {
      try {
          const frameDocument = frame.contentDocument || frame.contentWindow.document;
          if (frameDocument) {
              result.push(frameDocument);
          }
      } catch (e) {
          // Skip cross-origin frames
          highlighterLogger.warn('Could not access frame content:', e.message);
      }
  });

  return result;
}

export function getAllElementsIncludingShadow(selectors, root = document) {
  const elements = [];

  getAllFrames(root).forEach(doc => {
    elements.push(...getAllDocumentElementsIncludingShadow(selectors, doc));
  })

  return elements;
}

/**
 * Finds the first element matching a CSS selector, traversing Shadow DOM if necessary
 * @param {string} selector - CSS selector to search for
 * @param {Element} [root=document] - Root element to start searching from
 * @returns {Element|null} - The first matching element or null if not found
 */
export function robustQuerySelector(selector, root = document, all = false) {
  // First try to find in light DOM
  let elements = all ? root.querySelectorAll(selector) : root.querySelector(selector);
  if (elements) return elements;
  
  // Get all shadow roots
  const shadowElements = Array.from(root.querySelectorAll('*'))
      .filter(el => el.shadowRoot);
  
  // Search through each shadow root until we find a match
  for (const el of shadowElements) {
      elements = robustQuerySelector(selector, el.shadowRoot, all);
      if (elements) return elements;
  }
  
  return all ? [] : null;
}


function isDecendent(parent, child) {
  let element = child;
  while (element && element !== parent && element.nodeType === Node.ELEMENT_NODE) {              
      element = getParentNode(element);      
  }
  return element === parent;
}

export function generateXPath(element) {
  return '/'+extractElementPath(element).map(item => `${item.tagName}${item.onlyChild ? '' : `[${item.index}]`}`).join('/');
}

export function generateCssPath(element) {
  return extractElementPath(element).map(item => `${item.tagName}:nth-of-type(${item.index})`).join(' > ');
}

// unified path generation for xpath and css selector
function extractElementPath(element) {
  if (!element) {
    highlighterLogger.error('ERROR: No element provided to generatePath');
    return [];
  }
  const path = [];
  // traversing up the DOM tree
  while (element && element.nodeType === Node.ELEMENT_NODE) {    
    let tagName = element.nodeName.toLowerCase();
        
    let sibling = element;
    let index = 1;
    
    while (sibling = sibling.previousElementSibling) {
      if (sibling.nodeName.toLowerCase() === tagName) index++;
    }
    sibling = element;
    
    let onlyChild = (index === 1);
    while (onlyChild && (sibling = sibling.nextElementSibling)) {
      if (sibling.nodeName.toLowerCase() === tagName) onlyChild = false;
    }
       
    // add a tuple with tagName, index (nth), and onlyChild    
    path.unshift({
      tagName: tagName,
      index: index,
      onlyChild: onlyChild      
    });    

    element = getParentNode(element);
  }
  
  return path;
}

export function cleanHTML(rawHTML) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(rawHTML, "text/html");

  function cleanElement(element) {
    const allowedAttributes = new Set([
      "role",
      "type",
      "class",
      "href",
      "alt",
      "title",
      "readonly",
      "checked",
      "enabled",
      "disabled",
    ]);

    [...element.attributes].forEach(attr => {
      const name = attr.name.toLowerCase();
      const value = attr.value;

      const isTestAttribute = /^(testid|test-id|data-test-id)$/.test(name);
      const isDataAttribute = name.startsWith("data-") && value;
      const isBooleanAttribute = ["readonly", "checked", "enabled", "disabled"].includes(name);

      if (!allowedAttributes.has(name) && !isDataAttribute && !isTestAttribute && !isBooleanAttribute) {
        element.removeAttribute(name);
      }
    });

    // Handle SVG content - more aggressive replacement
    if (element.tagName.toLowerCase() === "svg") {
      // Remove all attributes except class and role
      [...element.attributes].forEach(attr => {
        const name = attr.name.toLowerCase();
        if (name !== "class" && name !== "role") {
          element.removeAttribute(name);
        }
      });
      element.innerHTML = "CONTENT REMOVED";
    } else {
      // Recursively clean child elements
      Array.from(element.children).forEach(cleanElement);
    }

    // Only remove empty elements that aren't semantic or icon elements
    const keepEmptyElements = ['i', 'span', 'svg', 'button', 'input'];
    if (!keepEmptyElements.includes(element.tagName.toLowerCase()) && 
        !element.children.length && 
        !element.textContent.trim()) {
      element.remove();
    }
  }

  // Process all elements in the document body
  Array.from(doc.body.children).forEach(cleanElement);
  return doc.body.innerHTML;
}

export function getContainingIFrame(element) {
  // If not in an iframe, return null
  if (element.ownerDocument.defaultView === window.top) {
    return null;
  }
  
  // Try to find the iframe in the parent document that contains our element
  try {
    const parentDocument = element.ownerDocument.defaultView.parent.document;
    const iframes = parentDocument.querySelectorAll('iframe');
    
    for (const iframe of iframes) {
      if (iframe.contentWindow === element.ownerDocument.defaultView) {
        return iframe;
      }
    }
  } catch (e) {
    // Cross-origin restriction
    return "Cross-origin iframe - cannot access details";
  }
  
  return null;
}

export function getAriaLabelledByText(elementInfo, includeHidden=true) {
  if (!elementInfo.element.hasAttribute('aria-labelledby')) return '';

  const ids = elementInfo.element.getAttribute('aria-labelledby').split(/\s+/);
  let labelText = '';

  //locate root (document or iFrame document if element is contained in an iframe)
  let root = document;
  if (elementInfo.iframe_selector) {      
    const frames = getAllDocumentElementsIncludingShadow('iframe', document);
    
    // Iterate over all frames and compare their CSS selectors
    for (const frame of frames) {
      const selector = generateCssPath(frame);
      if (selector === elementInfo.iframe_selector) {
        root = frame.contentDocument || frame.contentWindow.document;        
        break;
      }
    } 
  }

  ids.forEach(id => {
    const el = robustQuerySelector(`#${CSS.escape(id)}`, root);
    if (el) {
      if (includeHidden || el.offsetParent !== null || getComputedStyle(el).display !== 'none') {
        labelText += el.textContent.trim() + ' ';
      }
    }
  });

  return labelText.trim();
}



const filterZeroDimensions = (elementInfo) => {
  const rect = elementInfo.bounding_box;
  //single pixel elements are typically faux controls and should be filtered too
  const hasSize = rect.width > 1 && rect.height > 1;
  const style = window.getComputedStyle(elementInfo.element);
  const isVisible = style.display !== 'none' && style.visibility !== 'hidden';
  
  if (!hasSize || !isVisible) {
    
    return false;
  }
  return true;
};

function filterNestedElements(nonZeroElements, parentCandidates, shouldKeepNestedElementFn) {
  /**
   * Filter elements based on parent/child relationships.
   * @param {Array} nonZeroElements - Elements sorted by depth (parents first)
   * @param {Set} parentCandidates - Set of all candidate parent CSS selectors (all nonZeroElements)
   * @param {Function} shouldKeepNestedElementFn - Function(element_info, parentPath, parentElementInfo) => boolean
   * @returns {Array} Filtered elements
   */
  return nonZeroElements.filter(element_info => {
    const parent = findClosestParent(parentCandidates, element_info);
    
    if (parent == null) {
      // No parent found, keep element
      return true;
    }
    
    // Find parent element_info
    const parentElementInfo = nonZeroElements.find(e => e.css_selector === parent);
    
    // Use custom function to determine if we should keep nested element
    return shouldKeepNestedElementFn(element_info, parent, parentElementInfo);
  });
}

function getDirectHandlers(element) {
  /**
   * Get direct handlers for an element (no bubbling check).
   * Returns array of handler objects.
   */
  const handlers = [];
  
  // Check onclick
  if (element.hasAttribute('onclick') || element.onclick) {
    handlers.push({
      type: 'onclick',
      source: element.hasAttribute('onclick') ? 'attribute' : 'property',
      value: element.getAttribute('onclick') || 'function'
    });
  }
  
  // Check React Fiber
  const reactKey = Object.keys(element).find(key => 
    key.startsWith('__reactFiber') || key.startsWith('__reactInternalInstance')
  );
  if (reactKey) {
    const fiber = element[reactKey];
    if (fiber?.memoizedProps?.onClick || fiber?.pendingProps?.onClick) {
      handlers.push({
        type: 'react-onClick',
        source: 'react-fiber',
        hasMemoized: !!fiber?.memoizedProps?.onClick,
        hasPending: !!fiber?.pendingProps?.onClick
      });
    }
  }
  
  return handlers;
}

function getElementHandlersWithBubbling(element, depth = 0) {
  /**
   * Get handler info including bubbling (recursive DOM traversal).
   * Uses actual DOM relationships (parentElement) instead of CSS selector parsing.
   * Cursor uses getComputedStyle() which already includes inheritance.
   * @param {HTMLElement} element - The element to check
   * @param {number} depth - Recursion depth (safety limit)
   */
  // Safety check: null/undefined element
  if (!element) {
    return { handlers: [], cursorPointer: false, handlerSource: 'none' };
  }
  
  // Safety check: prevent infinite recursion (circular parent references)
  if (depth > 100) {
    throw new Error(`Recursion depth limit exceeded (${depth}) in getElementHandlersWithBubbling. Possible circular parent reference. Element: ${element.tagName || 'unknown'}`);
  }
  
  const directHandlers = getDirectHandlers(element);
  
  // If element has direct handlers, return them
  if (directHandlers.length > 0) {
    // Cursor: getComputedStyle already includes inheritance!
    let cursorPointer = false;
    try {
      const style = window.getComputedStyle(element);
      cursorPointer = style.cursor === 'pointer';
    } catch (e) {
      // getComputedStyle can fail in edge cases (e.g., detached elements)
      // Default to false
    }
    
    return {
      handlers: directHandlers,
      cursorPointer: cursorPointer,
      handlerSource: 'direct'
    };
  }
  
  // No direct handlers - check parent for bubbled handlers (using DOM, not CSS selectors)
  if (element.parentElement) {
    const parentResult = getElementHandlersWithBubbling(element.parentElement, depth + 1);
    if (parentResult.handlers.length > 0) {
      // Parent has handlers that would bubble to this element
      // Cursor: getComputedStyle already includes inheritance!
      let cursorPointer = false;
      try {
        const style = window.getComputedStyle(element);
        cursorPointer = style.cursor === 'pointer';
      } catch (e) {
        // getComputedStyle can fail in edge cases (e.g., detached elements)
        // Default to false
      }
      
      return {
        handlers: parentResult.handlers.map(h => ({
          ...h,
          source: 'bubbled'
        })),
        cursorPointer: cursorPointer,
        handlerSource: 'bubbled'
      };
    }
  }
  
  // No handlers at all
  let cursorPointer = false;
  try {
    const style = window.getComputedStyle(element);
    cursorPointer = style.cursor === 'pointer';
  } catch (e) {
    // getComputedStyle can fail in edge cases (e.g., detached elements)
    // Default to false
  }
  
  return {
    handlers: [],
    cursorPointer: cursorPointer,
    handlerSource: 'none'
  };
}

function normalizeHandlers(handlers) {
  /**
   * Normalize handlers for comparison (ignore source field, deduplicate).
   * Returns sorted array of unique normalized handler objects.
   */
  const seen = new Set();
  const normalized = [];
  
  for (const h of handlers) {
    // Safety check: skip malformed handlers
    if (!h || !h.type) continue;
    
    const key = h.type === 'react-onClick' 
      ? 'react-onClick' 
      : `${h.type}:${h.value || 'function'}`;
    
    if (!seen.has(key)) {
      seen.add(key);
      normalized.push({
        type: h.type,
        value: h.type === 'react-onClick' ? undefined : (h.value || 'function')
      });
    }
  }
  
  return normalized.sort((a, b) => {
    if (a.type !== b.type) return a.type.localeCompare(b.type);
    return (a.value || '').localeCompare(b.value || '');
  });
}

function compareHandlersForUniquify(childHandlers, parentHandlers) {
  /**
   * Compare handlers between child and parent for uniquification.
   * Returns true if handlers are different (should keep child), false if same (can unify).
   */
  const childNormalized = normalizeHandlers(childHandlers.handlers);
  const parentNormalized = normalizeHandlers(parentHandlers.handlers);
  
  const handlersEqual = JSON.stringify(childNormalized) === JSON.stringify(parentNormalized);
  const cursorEqual = childHandlers.cursorPointer === parentHandlers.cursorPointer;
  
  // If handlers or cursor differ, keep child (don't unify)
  return !handlersEqual || !cursorEqual;
}

export function uniquifyElements(elements) {
  highlighterLogger.debug(`Starting uniquification with ${elements.length} elements`);

  // Filter out testing infrastructure elements first
  const filteredInfrastructure = elements.filter(element_info => {
    // Skip the highlight-overlay element completely - it's part of the testing infrastructure
    if (element_info.element.id === 'highlight-overlay' || 
        (element_info.css_selector && element_info.css_selector.includes('#highlight-overlay'))) {
      highlighterLogger.debug('Filtered out testing infrastructure element:', element_info.css_selector);
      return false;
    }
    
    // Filter out UI framework container/manager elements
    const el = element_info.element;
    // UI framework container checks - generic detection for any framework
    if ((el.getAttribute('data-rendered-by') || 
         el.getAttribute('data-reactroot') || 
         el.getAttribute('ng-version') || 
         el.getAttribute('data-component-id') ||
         el.getAttribute('data-root') ||
         el.getAttribute('data-framework')) && 
        (el.className && 
         typeof el.className === 'string' && 
         (el.className.includes('Container') || 
          el.className.includes('container') || 
          el.className.includes('Manager') || 
          el.className.includes('manager')))) {
      highlighterLogger.debug('Filtered out UI framework container element:', element_info.css_selector);
      return false;
    }
    
    // Direct filter for framework container elements that shouldn't be interactive
    // Consolidating multiple container detection patterns into one efficient check
    const isFullViewport = element_info.bounding_box && 
                         element_info.bounding_box.x <= 5 && 
                         element_info.bounding_box.y <= 5 && 
                         element_info.bounding_box.width >= (window.innerWidth * 0.95) && 
                         element_info.bounding_box.height >= (window.innerHeight * 0.95);
                         
    // Empty content check
    const isEmpty = !el.innerText || el.innerText.trim() === '';
    
    // Check if it's a framework container element
    if (element_info.element.tagName === 'DIV' && 
        isFullViewport && 
        isEmpty && 
        (
          // Pattern matching for root containers
          (element_info.xpath && 
            (element_info.xpath.match(/^\/html\[\d+\]\/body\[\d+\]\/div\[\d+\]\/div\[\d+\]$/) || 
             element_info.xpath.match(/^\/\/\*\[@id='[^']+'\]\/div\[\d+\]$/))) ||
          
          // Simple DOM structure
          (element_info.css_selector.split(' > ').length <= 4 && element_info.depth <= 5) ||
          
          // Empty or container-like classes
          (!el.className || el.className === '' || 
           (typeof el.className === 'string' && 
            (el.className.includes('overlay') || 
             el.className.includes('container') || 
             el.className.includes('wrapper'))))
        )) {
      highlighterLogger.debug('Filtered out framework container element:', element_info.css_selector);
      return false;
    }
    
    return true;
  });

  // First filter out elements with zero dimensions
  const nonZeroElements = filteredInfrastructure.filter(filterZeroDimensions);
  // sort by CSS selector depth so parents are processed first
  nonZeroElements.sort((a, b) => a.depth - b.depth);
  highlighterLogger.debug(`After dimension filtering: ${nonZeroElements.length} elements remain (${elements.length - nonZeroElements.length} removed)`);
  
  // Create parent candidate set with ALL nonZeroElements (before filtering)
  // This allows findClosestParent to find any element as a parent, even if it gets filtered out later
  const parentCandidates = new Set(nonZeroElements.map(e => e.css_selector));
  
  // Filter nested elements using handler comparison and standard logic
  const filteredByParent = filterNestedElements(nonZeroElements, parentCandidates, (element_info, parent, parentElementInfo) => {
    return shouldKeepNestedElement(element_info, parent, parentElementInfo);
  });

  highlighterLogger.debug(`After parent/child filtering: ${filteredByParent.length} elements remain (${nonZeroElements.length - filteredByParent.length} removed)`);

  // Final overlap filtering
  const filteredResults = filteredByParent.filter(element => {

     // Look for any element that came BEFORE this one in the array
    const hasEarlierOverlap = filteredByParent.some(other => {
      // Only check elements that came before (lower index)
      if (filteredByParent.indexOf(other) >= filteredByParent.indexOf(element)) {
        return false;
      }
      
      const isOverlapping = areElementsOverlapping(element, other); 
      return isOverlapping;
    });  

    // Keep element if it has no earlier overlapping elements
    return !hasEarlierOverlap;
  });
  
  
  
  // Check for overlay removal
  highlighterLogger.debug(`After filtering: ${filteredResults.length} (${filteredByParent.length - filteredResults.length} removed by overlap)`);
  
  const nonOverlaidElements = filteredResults.filter(element => {
    return !isOverlaid(element);
  });

  highlighterLogger.debug(`Final elements after overlay removal: ${nonOverlaidElements.length} (${filteredResults.length - nonOverlaidElements.length} removed)`);
  
  return nonOverlaidElements;

}



const areElementsOverlapping = (element1, element2) => {
  if (element1.css_selector === element2.css_selector) {
    return true;
  }
  
  const box1 = element1.bounding_box;
  const box2 = element2.bounding_box;
  
  return box1.x === box2.x &&
         box1.y === box2.y &&
         box1.width === box2.width &&
         box1.height === box2.height;
        //  element1.text === element2.text &&
        //  element2.tag === 'a';
};

function findClosestParent(parentCandidates, element_info) {  
  // Split the CSS selector into segments
  const segments = element_info.css_selector.split(' > ');
  
  // Try increasingly shorter paths until we find one in the parentCandidates set
  for (let i = segments.length - 1; i > 0; i--) {
    const parentPath = segments.slice(0, i).join(' > ');
    if (parentCandidates.has(parentPath)) {
      return parentPath;
    }
  }

  return null;
}

function shouldKeepNestedElement(elementInfo, parentPath, parentElementInfo) {
  // Check special cases FIRST (before handler comparison)
  const parentSegments = parentPath.split(' > ');

  // If parent is a link, don't keep child
  const isParentLink = /^a(:nth-of-type\(\d+\))?$/.test(parentSegments[parentSegments.length - 1]);
  if (isParentLink) {
    return false;    
  }
  
  // If this is a checkbox/radio input inside a label, don't keep the input (keep label instead)
  if (elementInfo.tag === 'input' && 
      (elementInfo.type === 'checkbox' || elementInfo.type === 'radio')) {
    const isParentLabel = /^label(:nth-of-type\(\d+\))?$/.test(parentSegments[parentSegments.length - 1]);
    if (isParentLabel) {
      return false;
    }
  }
  
  // Now check if handlers differ (only if special cases don't apply)
  if (parentElementInfo) {
    const childHandlers = getElementHandlersWithBubbling(elementInfo.element);
    const parentHandlers = getElementHandlersWithBubbling(parentElementInfo.element);
    
    // If handlers differ, keep child (don't unify)
    if (compareHandlersForUniquify(childHandlers, parentHandlers)) {
      return true;
    }
  }
  
  return isFormControl(elementInfo) || isDropdownItem(elementInfo) || isTableCell(elementInfo);
}

function isTableCell(elementInfo) {
  const element = elementInfo.element;
  if(!element || !(element instanceof HTMLElement)) {
    return false;
  }
  const validTags = new Set(['td', 'th']);
  const validRoles = new Set(['cell', 'gridcell', 'columnheader', 'rowheader']);
  
  const tag = element.tagName.toLowerCase();
  const role = element.getAttribute('role')?.toLowerCase();

  if (validTags.has(tag) || (role && validRoles.has(role))) {
    return true;
  }
  return false;
  
}

function isOverlaid(elementInfo) {
  const element = elementInfo.element;
  const boundingRect = elementInfo.bounding_box;
  

  
  
  // Create a diagnostic logging function that only logs when needed
  const diagnosticLog = (...args) => {
    if (true) { // set to true for debugging
      highlighterLogger.debug('[OVERLAY-DEBUG]', ...args);
    }
  };

  // Special handling for tooltips
  if (elementInfo.element.className && typeof elementInfo.element.className === 'string' && 
      elementInfo.element.className.includes('tooltip')) {
    diagnosticLog('Element is a tooltip, not considering it overlaid');
    return false;
  }
  
  
    
      // Get element at the center point to check if it's covered by a popup/modal
      const middleX = boundingRect.x + boundingRect.width/2;
      const middleY = boundingRect.y + boundingRect.height/2;
      const elementAtMiddle = element.ownerDocument.elementFromPoint(middleX, middleY);
      
      if (elementAtMiddle && 
          elementAtMiddle !== element && 
          !isDecendent(element, elementAtMiddle) && 
          !isDecendent(elementAtMiddle, element)) {

      
      return true;
    }
      
      
    return false;
  
  }
  
  
/*************************/
/*** EXPERIMENTAL CODE ***/
/*************************/

/**
 * Detects popup/modal elements on a webpage and returns their bounding rectangles
 * Looks for common indicators like:
 * - Elements with modal/popup/dialog in class names or IDs
 * - Elements with high z-index
 * - Dialog HTML elements
 * - Elements with ARIA roles for dialogs
 * - Elements positioned fixed/absolute and centered
 * @returns {Array<{element: HTMLElement, rect: DOMRect}>} Array of detected elements and their bounding rectangles
 */
function detectPopups() {
  const results = [];
  
  // Helper to check if element is likely visible
  const isVisible = (element) => {
      const style = window.getComputedStyle(element);
      const boundingRect = element.getBoundingClientRect();
      return style.display !== 'none' && 
             style.visibility !== 'hidden' && 
             style.opacity !== '0' &&
             boundingRect.width > 0 &&
             boundingRect.height > 0;
  };

  // Helper to check if element is centered
  const isCentered = (element) => {
      const style = window.getComputedStyle(element);
      return (style.position === 'fixed' || style.position === 'absolute') &&
             (style.left === '50%' || style.transform.includes('translate')) &&
             (style.top === '50%' || style.transform.includes('translate'));
  };

  // Find elements by common class names and IDs
  const modalSelectors = [
      '[class*="modal"]',
      '[class*="popup"]',
      '[class*="dialog"]',
      '[id*="modal"]',
      '[id*="popup"]',
      '[id*="dialog"]',
      'dialog',
      '[role="dialog"]',
      '[role="alertdialog"]'
  ];

  const potentialPopups = document.querySelectorAll(modalSelectors.join(','));

  potentialPopups.forEach(element => {
      if (!isVisible(element)) return;

      const style = window.getComputedStyle(element);
      const zIndex = parseInt(style.zIndex);

      // Check various characteristics that suggest this is a popup/modal
      const isLikelyPopup = 
          element.tagName.toLowerCase() === 'dialog' ||
          element.getAttribute('role') === 'dialog' ||
          element.getAttribute('role') === 'alertdialog' ||
          (zIndex && zIndex > 10) ||
          isCentered(element);

      if (isLikelyPopup) {
          results.push({
              element,
              rect: element.getBoundingClientRect()
          });
      }
  });

  // Look for high z-index elements that might be popups
  const allElements = document.querySelectorAll('*');
  allElements.forEach(element => {
      if (!isVisible(element)) return;
      
      const style = window.getComputedStyle(element);
      const zIndex = parseInt(style.zIndex);

      if (zIndex > 100 && !results.some(r => r.element === element)) {
          results.push({
              element,
              rect: element.getBoundingClientRect()
          });
      }
  });

  //filter out children
  return results.filter(result => {
      // Check if any other element in the list is an ancestor of this element
      const hasAncestorInList = results.some(potentialAncestor => {
          // Skip comparing element with itself
          if (potentialAncestor.element === result.element) {
              return false;
          }
          // Check if potentialAncestor contains element
          return potentialAncestor.element.contains(result.element);
      });
      
      // Keep element only if it has no ancestors in the list
      return !hasAncestorInList;
  });
}

// Example usage
function findAndLogPopups() {
  const popups = detectPopups();
  
  if (popups.length === 0) {
      highlighterLogger.debug('No popups detected');
      return;
  }

  popups.forEach((popup, index) => {
      const rect = popup.rect;
      highlighterLogger.log(`Popup ${index + 1}:`);
      highlighterLogger.log('Element:', popup.element);
      highlighterLogger.log('Position:', {
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height
      });
  });
}



/**
 * Get the “best” short, unique, and robust CSS selector for an element.
 * 
 * @param {Element} element
 * @returns {string} A selector guaranteed to find exactly that element in its context
 */
export function getRobustSelector(element) {
  // 1. Figure out the real “root” (iframe doc, shadow root, or main doc)
  const root = (() => {
    const rootNode = element.getRootNode();
    if (rootNode instanceof ShadowRoot) {
      return rootNode;
    }
    return element.ownerDocument;
  })();

  // 2. Options to bias toward stable attrs and away from auto-generated classes
  const options = {
    root,
    // only use data-*, id or aria-label by default
    attr(name, value) {
      if (name === 'id' || name.startsWith('data-') || name === 'aria-label') {
        return true;
      }
      return false;
    },
    // skip framework junk
    filter(name, value) {
      if (name.startsWith('ng-') || name.startsWith('_ngcontent') || /^p-/.test(name)) {
        return false;
      }
      return true;
    },
    // let finder try really short seeds
    seedMinLength: 1,
    optimizedMinLength: 1,
  };

  let selector;
  try {
    selector = finder(element, options);
    // 3. Verify it really works in the context
    const found = root.querySelectorAll(selector);
    if (found.length !== 1 || found[0] !== element) {
      throw new Error('not unique or not found');
    }
    return selector;
  } catch (err) {
    // 4. Fallback: full path (you already have this utility)
    highlighterLogger.warn('[getRobustSelector] finder failed, falling back to full path:', err);
    return generateCssPath(element); // you’d import or define this elsewhere
  }
}

/**
 * Checks if an element is scrollable (has scrollable content)
 * 
 * @param element - The element to check
 * @returns boolean indicating if the element is scrollable
 */
/* export function isScrollableContainer(element) {
  if (!element) return false;
  
  const style = window.getComputedStyle(element);
  
  // Reliable way to detect if an element has scrollbars or is scrollable
  const hasScrollHeight = element.scrollHeight > element.clientHeight;
  const hasScrollWidth = element.scrollWidth > element.clientWidth;
  
  // Check actual style properties
  const hasOverflowY = style.overflowY === 'auto' || 
                      style.overflowY === 'scroll' || 
                      style.overflowY === 'overlay';
  const hasOverflowX = style.overflowX === 'auto' || 
                      style.overflowX === 'scroll' || 
                      style.overflowX === 'overlay';
  
  // Check common class names and attributes for scrollable containers across frameworks
  const hasScrollClasses = element.classList.contains('scroll') || 
                          element.classList.contains('scrollable') ||
                          element.classList.contains('overflow') ||
                          element.classList.contains('overflow-auto') ||
                          element.classList.contains('overflow-scroll') ||
                          element.getAttribute('data-scrollable') === 'true';
                          
  // Check for height/max-height constraints that often indicate scrolling content
  const hasHeightConstraint = style.maxHeight && 
                             style.maxHeight !== 'none' && 
                             style.maxHeight !== 'auto';
  
  // An element is scrollable if it has:
  // 1. Actual scrollbars in use (most reliable check) OR
  // 2. Overflow styles allowing scrolling AND content that would require scrolling
  return (hasScrollHeight && hasOverflowY) || 
         (hasScrollWidth && hasOverflowX) ||
         (hasScrollClasses && (hasScrollHeight || hasScrollWidth)) ||
         (hasHeightConstraint && hasScrollHeight);
} */

export function isScrollableContainer(el) {
  if (!el || el === document.body) return false;

  const style = getComputedStyle(el);

  const canScroll = (overflowProp) => {
    return overflowProp === 'auto' ||
           overflowProp === 'scroll' ||
           overflowProp === 'overlay';
  };

  //check if the element can scroll on either axis
  if (!canScroll(style.overflowY) && !canScroll(style.overflowX)) return false;
  return el.scrollHeight - el.clientHeight > 1 || el.scrollWidth - el.clientWidth > 1;
}
/**
 * Detects scrollable containers that are ancestors of the target element
 * 
 * This function traverses up the DOM tree from the target element and identifies
 * all scrollable containers (elements that have scrollable content).
 * 
 * @param target - The target element to start the search from
 * @returns Array of objects with selector and scroll properties
 */
export function detectScrollableContainers(target) {
  const scrollableContainers = [];
  
  if (!target) {
    return scrollableContainers;
  }
  
  highlighterLogger.debug('🔍 [detectScrollableContainers] Starting detection for target:', target.tagName, target.id, target.className);
  
  // Detect if target is inside an iframe
  const iframe = getContainingIFrame(target);
  const iframe_selector = iframe ? generateCssPath(iframe) : "";
  
  highlighterLogger.debug('🔍 [detectScrollableContainers] Iframe context:', iframe ? 'inside iframe' : 'main document', 'selector:', iframe_selector);
  
  // Start from the target element and traverse up the DOM tree
  let currentElement = target;
  let depth = 0;
  const MAX_DEPTH = 15; // Limit traversal depth to avoid infinite loops
  
  while (currentElement && currentElement.nodeType === Node.ELEMENT_NODE && depth < MAX_DEPTH) {        
    // Check if the current element is scrollable
    if (isScrollableContainer(currentElement)) {
      highlighterLogger.debug('🔍 [detectScrollableContainers] Found scrollable container at depth', depth, ':', currentElement.tagName, currentElement.id, currentElement.className);
      
      const container = {
        containerEl: currentElement,
        selector: generateCssPath(currentElement),
        iframe_selector: iframe_selector,
        scrollTop: currentElement.scrollTop,
        scrollLeft: currentElement.scrollLeft,
        scrollHeight: currentElement.scrollHeight,
        scrollWidth: currentElement.scrollWidth,
        clientHeight: currentElement.clientHeight,
        clientWidth: currentElement.clientWidth
      };
      
      scrollableContainers.push(container);
    }
    
    // Move to parent element
    currentElement = getParentNode(currentElement);
    
    depth++;
  }
  
  highlighterLogger.debug('🔍 [detectScrollableContainers] Detection complete. Found', scrollableContainers.length, 'scrollable containers');
  return scrollableContainers;
}